import type {
  CanvasOperationProgressPayload,
  CanvasOperationProbeRequestPayload,
  CanvasOperationProbeResultPayload,
} from '@hector/shared-types';

/** One browser-side liveness owner for a request already fenced by the backend. */
export interface CanvasOperationProgressHandle {
  queued(): void;
  running(): void;
  completedUnit(progress: { completedUnits: number; totalUnits: number }): void;
  finish(): void;
}

type OperationState = {
  requestId: string;
  conversationId: string;
  mountEpoch: number;
  expectedRevision?: string;
  sequence: number;
  phase: 'accepted' | 'queued' | 'running';
  completedUnits?: number;
  totalUnits?: number;
  heartbeat: ReturnType<typeof setInterval> | null;
  finished: boolean;
};

type StartInput = {
  requestId: string;
  conversationId: string;
  mountEpoch: number;
  expectedRevision?: string;
};

type RegistryOptions = {
  /** Injectable so the protocol's heartbeat behaviour is independently testable. */
  heartbeatMs?: number;
  /** The owner decides whether the current socket negotiated and can send the frame. */
  emit: (payload: CanvasOperationProgressPayload) => void;
};

const DEFAULT_HEARTBEAT_MS = 5_000;

/**
 * Emits only correlation-safe liveness for Canvas operations.
 *
 * The registry owns no result and no document data. `finish()` therefore stops
 * progress without publishing a terminal signal: the validated SuperDoc receipt
 * remains the sole authority that can settle the backend request.
 */
export class CanvasOperationProgressRegistry {
  private readonly heartbeatMs: number;
  private readonly active = new Map<string, OperationState>();

  constructor(private readonly options: RegistryOptions) {
    this.heartbeatMs = options.heartbeatMs ?? DEFAULT_HEARTBEAT_MS;
  }

  start(input: StartInput): CanvasOperationProgressHandle {
    const state: OperationState = {
      ...input,
      sequence: 0,
      phase: 'accepted',
      heartbeat: null,
      finished: false,
    };
    const prior = this.active.get(input.requestId);
    // A duplicate requestId cannot safely share a sequence or mount fence. It
    // is not expected from UUID-producing backend tools, but failing closed
    // here ensures a stale callback cannot inherit a new operation's channel.
    if (prior) this.finish(prior);
    this.active.set(input.requestId, state);
    this.publish(state, 'accepted');

    return {
      queued: () => {
        if (!this.isActive(state) || state.phase !== 'accepted') return;
        this.publish(state, 'queued');
      },
      running: () => {
        if (!this.isActive(state) || state.phase !== 'queued') return;
        this.publish(state, 'running');
        state.heartbeat = setInterval(() => {
          if (!this.isActive(state) || state.phase !== 'running') return;
          this.publish(state, 'running');
        }, this.heartbeatMs);
      },
      completedUnit: ({ completedUnits, totalUnits }) => {
        if (!this.isActive(state) || state.phase !== 'running') return;
        if (
          !Number.isInteger(completedUnits)
          || !Number.isInteger(totalUnits)
          || completedUnits < 0
          || totalUnits <= 0
          || completedUnits > totalUnits
          || (state.totalUnits !== undefined && state.totalUnits !== totalUnits)
          || (state.completedUnits !== undefined && completedUnits < state.completedUnits)
        ) return;
        state.completedUnits = completedUnits;
        state.totalUnits = totalUnits;
        this.publish(state, 'running');
      },
      finish: () => this.finish(state),
    };
  }

  /** Stops every request when its socket dies or its connection slot is removed. */
  cancelAll(): void {
    for (const state of this.active.values()) this.finish(state);
  }

  /**
   * Reads only this registry's exact request ownership. It never opens a
   * Canvas, reads a document, invokes SuperDoc, or derives activity from a
   * writer/autosave counter. A missing entry says only that no matching
   * operation is currently registered; it never says whether a mutation won.
   */
  probe(input: CanvasOperationProbeRequestPayload): CanvasOperationProbeResultPayload {
    const state = this.active.get(input.requestId);
    const matchesExactOperation = state !== undefined
      && this.isActive(state)
      && state.conversationId === input.conversationId
      && state.mountEpoch === input.mountEpoch
      && state.expectedRevision === input.expectedRevision;
    const identity = {
      probeId: input.probeId,
      requestId: input.requestId,
      conversationId: input.conversationId,
      mountEpoch: input.mountEpoch,
      ...(input.expectedRevision !== undefined ? { expectedRevision: input.expectedRevision } : {}),
    };
    if (!matchesExactOperation) return { ...identity, status: 'not_running' };
    return {
      ...identity,
      status: 'active',
      phase: state.phase,
      ...(state.completedUnits !== undefined && state.totalUnits !== undefined
        ? { completedUnits: state.completedUnits, totalUnits: state.totalUnits }
        : {}),
    };
  }

  private isActive(state: OperationState): boolean {
    return !state.finished && this.active.get(state.requestId) === state;
  }

  private finish(state: OperationState): void {
    if (state.finished) return;
    state.finished = true;
    if (state.heartbeat !== null) clearInterval(state.heartbeat);
    state.heartbeat = null;
    if (this.active.get(state.requestId) === state) this.active.delete(state.requestId);
  }

  private publish(state: OperationState, phase: OperationState['phase']): void {
    state.phase = phase;
    state.sequence += 1;
    const payload: CanvasOperationProgressPayload = {
      requestId: state.requestId,
      conversationId: state.conversationId,
      mountEpoch: state.mountEpoch,
      ...(state.expectedRevision !== undefined ? { expectedRevision: state.expectedRevision } : {}),
      sequence: state.sequence,
      phase,
      ...(state.completedUnits !== undefined && state.totalUnits !== undefined
        ? { completedUnits: state.completedUnits, totalUnits: state.totalUnits }
        : {}),
    };
    // Progress is advisory. A transient UI send error must never interrupt the
    // actual document operation or fabricate a terminal outcome.
    try {
      this.options.emit(payload);
    } catch {
      // The backend's total deadline remains the authoritative failure path.
    }
  }
}
